import { useContext, useEffect } from "react";

import { GlobalContext } from "api/GlobalContext.js";

const UPLOAD_PATH = "/v1/notes/capture";
const NOTE_CAPTURE_FEATURE = "get_all_notes";

export default function NotesUploadBridge() {
  const { apiClient } = useContext(GlobalContext);

  useEffect(() => {
    if (window === window.parent) return;

    const sendFeatureFlags = (features) => {
      const payload = {
        type: "SET_FEATURE_FLAGS",
        msg: {
          features,
        },
      };
      window.parent.postMessage(
        {
          __comp: true,
          type: "DOM_REQUEST",
          id: `feature_flags_${Date.now()}`,
          payload,
        },
        "*",
      );
    };
    const syncFeatureFlags = () => {
      sendFeatureFlags({
        [NOTE_CAPTURE_FEATURE]: apiClient?.features?.[NOTE_CAPTURE_FEATURE] === true,
        pte_custom_note_types: apiClient?.features?.pte_custom_note_types === true,
        cora_rom: apiClient?.features?.cora_rom === true,
        new_rt_integration: apiClient?.features?.new_rt_integration === true,
        new_webpt_soap2_integration: apiClient?.features?.new_webpt_soap2_integration === true,
        new_webpt_integration: apiClient?.features?.new_webpt_integration === true,
        webpt2_chrome_freeze_fix: apiClient?.features?.webpt2_chrome_freeze_fix === true,
        raintree_note_capture: apiClient?.features?.raintree_note_capture === true,
        // Picks which Prompt integration note capture keys the note like.
        new_prompt_integration: apiClient?.features?.new_prompt_integration === true,
      });
    };

    const originalReset = apiClient?.reset;
    if (typeof originalReset === "function") {
      apiClient.reset = (...args) => {
        sendFeatureFlags({});
        return originalReset.apply(apiClient, args);
      };
    }
    apiClient?.onProfileUpdated?.(syncFeatureFlags);
    syncFeatureFlags();
    return () => {
      if (typeof originalReset === "function") {
        apiClient.reset = originalReset;
      }
      apiClient?.offProfileUpdated?.(syncFeatureFlags);
    };
  }, [apiClient]);

  useEffect(() => {
    if (window === window.parent) return;

    const handleUpload = async (event) => {
      const message = event.data;
      if (
        event.source !== window.parent ||
        !isSupportedNoteCaptureSource(event, message) ||
        !message?.__comp ||
        message.type !== "UPLOAD_CAPTURED_NOTES"
      ) {
        return;
      }

      const sessionKey = apiClient?.sessionKey;
      const sessionEmail = apiClient?.sessionEmail;
      const baseUrl = apiClient?.apiClient?.defaults?.baseURL;
      const uploadTarget = `${String(baseUrl || "").replace(/\/$/, "")}${UPLOAD_PATH}`;

      if (!sessionEmail || !Number.isInteger(Number(sessionKey)) || !baseUrl) {
        sendResponse(event, message.payload?.id, 0);
        return;
      }

      if (apiClient?.features?.[NOTE_CAPTURE_FEATURE] !== true) {
        sendResponse(event, message.payload?.id, 0);
        return;
      }

      let status = 0;
      try {
        const response = await fetch(uploadTarget, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionKey,
            sessionEmail,
            notes: message.payload?.notes || [],
          }),
          keepalive: Boolean(message.payload?.keepalive),
        });
        status = response.status;
      } catch (_) {
        status = 0;
      }

      sendResponse(event, message.payload?.id, status);
    };

    window.addEventListener("message", handleUpload);
    return () => window.removeEventListener("message", handleUpload);
  }, [apiClient]);

  return null;
}

function isSupportedNoteCaptureSource(event, message) {
  if (isSupportedNoteCaptureOrigin(event.origin)) return true;

  try {
    return (
      new URL(event.origin).protocol === "chrome-extension:" &&
      isSupportedNoteCaptureOrigin(message?.__compParentOrigin)
    );
  } catch (_) {
    return false;
  }
}

function isSupportedNoteCaptureOrigin(origin) {
  try {
    const url = new URL(origin);
    return (
      url.protocol === "https:" &&
      (url.hostname === "insight.clinicient.com" ||
        url.hostname === "test2-insight.clinicient.com" ||
        url.hostname === "corahealth.com" ||
        url.hostname.endsWith(".corahealth.com") ||
        url.hostname.endsWith(".empoweremr.com") ||
        url.hostname === "raintreeinc.com" ||
        url.hostname.endsWith(".raintreeinc.com") ||
        url.hostname === "empoweremr.com" ||
        url.hostname === "app.webpt.com" ||
        url.hostname === "emr.webpt.com" ||
        url.hostname.endsWith(".stridethera.com") ||
        url.hostname === "theraofficeweb.com" ||
        url.hostname.endsWith(".theraofficeweb.com") ||
        url.hostname === "go.promptemr.com")
    );
  } catch (_) {
    return false;
  }
}

function sendResponse(event, id, status) {
  event.source?.postMessage(
    {
      __comp: true,
      type: "CAPTURED_NOTES_UPLOAD_RESPONSE",
      id,
      status,
    },
    event.origin || "*",
  );
}
